const LAST_READING_UID_KEY = "bible_reading_uid";
export const EVENT_READING_USER_CHANGED = "bible_reading_user_changed";

const GUEST_READING_KEYS = [
  "bible_read_chapters",
  "bible_last_read",
  "bible_recent_chapters",
];

const TRANSIENT_UNSCOPED_KEYS = [
  "bible_current_reader_chapter",
  "bible_reader_verse_focus",
];

const LEGACY_UNSCOPED_SEARCH_HISTORY = "bible_search_history";

export const normalizeReadingUid = (uid) => {
  if (!uid || uid === "local_guest_user" || uid === "local_user") return "";
  return String(uid);
};

const persistReadingUid = (uid) => {
  if (typeof localStorage === "undefined") return;
  try {
    if (uid) localStorage.setItem(LAST_READING_UID_KEY, uid);
    else localStorage.removeItem(LAST_READING_UID_KEY);
  } catch (e) {
    // ignore quota / private-mode errors
  }
};

const readPersistedReadingUid = () => {
  if (typeof localStorage === "undefined") return "";
  try {
    return normalizeReadingUid(localStorage.getItem(LAST_READING_UID_KEY) || "");
  } catch (e) {
    return "";
  }
};

let activeUid = readPersistedReadingUid();

export const getReadingStorageUser = () => activeUid;

export const getReadingStorageKey = (base, uid = activeUid) => {
  const scope = normalizeReadingUid(uid);
  return scope ? `${base}:${scope}` : base;
};

const notifyReadingScopeChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("bible_read_status_changed"));
  window.dispatchEvent(new Event("bible_last_read_changed"));
};

export const clearGuestReadingKeys = () => {
  if (typeof window === "undefined") return;
  try {
    GUEST_READING_KEYS.forEach((base) => localStorage.removeItem(base));
  } catch (e) {
    // ignore quota / private-mode errors
  }
};

const removeStorageKey = (store, key) => {
  try {
    store.removeItem(key);
  } catch (e) {
    // ignore quota / private-mode errors
  }
};

// Session leftovers (current chapter, verse focus) are not uid-keyed in older
// builds. Drop the unscoped copies on every account change so a same-tab
// logout → login cannot feed the previous reader's chapter into the picker.
export const clearTransientReaderCaches = () => {
  if (typeof window === "undefined") return;
  if (typeof sessionStorage !== "undefined") {
    TRANSIENT_UNSCOPED_KEYS.forEach((key) => removeStorageKey(sessionStorage, key));
  }
  if (typeof localStorage !== "undefined") {
    removeStorageKey(localStorage, "bible_reader_verse_focus");
  }
};

// Pre-scoping builds kept one shared bible_search_history key. After sign-in
// that leftover must not be read or copied onto the new account (guest keeps
// using the unscoped key; signed-in users only use bible_search_history:uid).
export const clearLegacyUnscopedSearchHistory = () => {
  if (typeof localStorage === "undefined") return;
  removeStorageKey(localStorage, LEGACY_UNSCOPED_SEARCH_HISTORY);
};

const adoptGuestReadingIfNeeded = () => {
  if (typeof window === "undefined" || !activeUid) return;
  try {
    const scopedReadKey = `bible_read_chapters:${activeUid}`;
    if (localStorage.getItem(scopedReadKey)) return;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith("bible_read_chapters:") && key !== scopedReadKey) {
        return;
      }
    }
    GUEST_READING_KEYS.forEach((base) => {
      const guest = localStorage.getItem(base);
      if (guest && !localStorage.getItem(`${base}:${activeUid}`)) {
        localStorage.setItem(`${base}:${activeUid}`, guest);
      }
    });
  } catch (e) {
    // ignore quota / private-mode errors
  }
};

export const setReadingStorageUser = (uid) => {
  const next = normalizeReadingUid(uid);
  if (next === activeUid) return;
  const previous = activeUid;
  activeUid = next;
  persistReadingUid(next);
  clearTransientReaderCaches();
  if (next) {
    // Search history is not adopted from the shared legacy key. Drop it so a
    // same-tab logout → login cannot keep showing the previous person's terms.
    clearLegacyUnscopedSearchHistory();
    // Only the first sign-in from a logged-out session may inherit leftover
    // guest keys. Switching A -> B must never copy another account's progress.
    if (!previous) {
      adoptGuestReadingIfNeeded();
    }
  } else {
    // Signing out must not leave the previous account's progress on the guest home.
    clearGuestReadingKeys();
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(EVENT_READING_USER_CHANGED, { detail: { uid: next, previous } })
    );
  }
  notifyReadingScopeChanged();
};
